import { useEffect } from 'react';

import { useIntl } from 'react-intl';
import { NavLink, useMatch } from 'react-router-dom';
import classNames from 'classnames';
import { Navbar, Badge } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { useLocation, useUpdateEffect } from 'react-use';

import {
  logoLight,
  bellWhite,
  profileWhite,
  signalWhite,
  personAddWhite,
  groupManagementWhite,
  groupManagementAddWhite,
  billingWhite,
  reportsWhite,
  report,
  support
} from 'assets';
import {
  AlertTypes,
  DeviceTypes,
  AccessRoles,
  AnalyticsTabs,
  BillingTabs,
  GroupManagementTabs
} from 'constants/enums';
import { getPath } from 'routes';
import { useCanMigratePatients, useNotifications, useRole } from 'hooks';
import { getAlertsCount, getOpenTicketsCount } from 'store/groups/service';
import { decrementAlertsCount, incrementAlertsCount } from 'store/notifications/reducer';
import { isEHRInviteEnabled } from 'utils/EHRs';
import { useAppSelector } from 'hooks';
import { getGroupByGid, isPrepaidOrg, selectCurrentGid } from 'store/groups/reducer';
import { isOrganization } from 'utils/Groups';

import packageJson from '../../../../../package.json';

const Sidebar = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const location = useLocation();
  const hideNavigation = useAppSelector((state) => state.settings.hideNavigation);
  const alertsCount = useAppSelector((state) => state.notifications.alertsCount);
  const alertsCountLoading = useAppSelector((state) => state.notifications.alertsCountLoading);
  const gid = useAppSelector(selectCurrentGid);
  const group = useAppSelector((state) => getGroupByGid(state, gid));
  const isOrg = group ? isOrganization(group) : false;
  const isAllGroups = gid === 'all';
  const prepaidOrg = useAppSelector(isPrepaidOrg);
  const isEhrInviteOnly = isEHRInviteEnabled(group);
  const { lastMessage } = useNotifications({ type: 'alerts' });

  const matchPatients = useMatch(getPath().dashboard.patients);
  const matchPatient = useMatch(getPath().dashboard.patient());
  const isMatchingPatients = matchPatients || matchPatient;

  const matchDevices = useMatch(getPath().dashboard.devices());
  const matchDevice = useMatch(getPath().dashboard.device());
  const isMatchingDevices = matchDevices || matchDevice;

  const canMigratePatients = useCanMigratePatients();

  const navigation = [
    {
      name: 'patientsKey',
      image: profileWhite,
      path: getPath().dashboard.patients,
      match: isMatchingPatients,
      role: useRole([AccessRoles.ADMIN])
    },
    {
      name: 'alertsKey',
      image: bellWhite,
      path: getPath().dashboard.alerts(AlertTypes.MEDICAL),
      match: useMatch(getPath().dashboard.alerts()),
      role: useRole([AccessRoles.ADMIN]),
      alertNotification: true
    },
    {
      name: 'tasksKey',
      image: report,
      path: getPath().dashboard.tasks(),
      match: useMatch(getPath().dashboard.tasks()),
      role: useRole([AccessRoles.ADMIN])
    },
    {
      name: 'devicesKey',
      image: signalWhite,
      path: getPath().dashboard.devices(DeviceTypes.ASSIGNED),
      match: isMatchingDevices,
      role: useRole([AccessRoles.ADMIN, AccessRoles.SYSOP])
    },
    {
      name: 'reportsKey',
      image: reportsWhite,
      path: getPath().dashboard.analytics(AnalyticsTabs.REPORTS),
      match: useMatch(getPath().dashboard.analytics()),
      role: useRole([AccessRoles.ADMIN, AccessRoles.GROUP_MANAGEMENT])
    },
    {
      name: 'onboardPatientKey',
      image: personAddWhite,
      path: getPath().dashboard.onboard,
      match: useMatch(getPath().dashboard.onboard),
      role: useRole([AccessRoles.ADMIN]) && !isEhrInviteOnly
    },
    {
      name: 'migrationImportTitleKey',
      image: personAddWhite,
      path: getPath().dashboard.importPatients,
      match: useMatch(getPath().dashboard.importPatients),
      role: canMigratePatients
    },
    {
      name: 'billingNavKey',
      image: billingWhite,
      path: getPath().dashboard.billing(BillingTabs.OVERVIEW),
      match: useMatch(getPath().dashboard.billing()),
      // Billing is its own grant: a group admin without BILLING must not see this (PHX-1295).
      role: useRole([AccessRoles.BILLING, AccessRoles.SYSOP]) && isOrg && prepaidOrg
    },
    {
      name: 'adminKey',
      image: groupManagementWhite,
      path: getPath().dashboard.groupManagement.layout(GroupManagementTabs.CONFIGURATION),
      match: useMatch(getPath().dashboard.groupManagement.layout()),
      role: useRole([AccessRoles.GROUP_MANAGEMENT, AccessRoles.SYSOP]) && isOrg
    },
    {
      name: 'supportTabKey',
      image: support,
      path: getPath().dashboard.support,
      match: useMatch(getPath().dashboard.support),
      role: useRole([AccessRoles.ADMIN]) && !isAllGroups
    },
    {
      // SYSOP only: provisioning a prepaid org from a signed contract is a CareSimple staff task,
      // and it needs no group context — it is what creates one (PHX-1295).
      name: 'onboardingNavKey',
      // The same building as Group management, plus a +: the two sat side by side in the sidebar
      // and read as the same entry (Tatiana, 2026-09-15).
      image: groupManagementAddWhite,
      path: getPath().dashboard.salesLedOnboarding,
      match: useMatch(getPath().dashboard.salesLedOnboarding),
      role: useRole([AccessRoles.SYSOP])
    }
  ];

  const isSysop = useRole([AccessRoles.SYSOP]);

  useEffect(() => {
    if (!isSysop) {
      dispatch(getAlertsCount(gid));
      if (gid !== 'all') {
        dispatch(getOpenTicketsCount({ gid }));
      }
    }
  }, [dispatch, gid, isSysop, location.pathname]);

  useUpdateEffect(() => {
    if (!alertsCountLoading) {
      if (lastMessage && ['new', 'open'].includes(lastMessage.status))
        dispatch(incrementAlertsCount());
      if (lastMessage?.status === 'closed') dispatch(decrementAlertsCount());
    }
  }, [lastMessage]);

  return (
    <div id="sidebar">
      <div id="logocontainer" className="bg-blue-dark">
        <img src={logoLight} alt={intl.formatMessage({ id: 'tactioSoftware' })} className="logo" />
      </div>
      <Navbar bg="blue-dark" id="navigationbar" className="p-0 pt-3">
        {!hideNavigation &&
          navigation.map((item, key) => {
            return (
              item.role && (
                <NavLink
                  data-test={`sidebar-${item.name}-link`}
                  key={key}
                  to={item.path}
                  className={classNames({
                    active: item.match
                  })}
                >
                  <div className="position-relative">
                    <img
                      data-test={`sidebar-${item.name}-img`}
                      src={item.image}
                      alt={intl.formatMessage({ id: item.name })}
                      className="block mx-auto mb-h-xxs"
                    />
                    <p>{intl.formatMessage({ id: item.name })}</p>
                    {item.alertNotification && !alertsCountLoading && (
                      <Badge
                        data-test="sidebar-alerts-notification"
                        bg="danger"
                        className="notifications"
                      >
                        {alertsCount > 0 && (alertsCount > 999 ? '999+' : alertsCount)}
                      </Badge>
                    )}
                    {/* {item.ticketNotification && !openTicketsCountLoading && (
                      <Badge
                        data-test="sidebar-tickets-notification"
                        bg="danger"
                        className="notifications"
                      >
                        {openTicketsCount > 0 &&
                          (openTicketsCount > 999 ? '999+' : openTicketsCount)}
                      </Badge>
                    )} */}
                  </div>
                </NavLink>
              )
            );
          })}
        <p className="fixed-sidebar-footer text-white">
          {intl.formatMessage({ id: 'versionKey' })} {packageJson.version}
        </p>
      </Navbar>
    </div>
  );
};

export default Sidebar;
